import { UIElement } from '../ui-element';
import {
    createElement,
    dispatchNativeEvent,
    hide,
    insertElements,
    show,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { Labels } from '../../global/labels';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIButton } from '../button/ui-button';
import type { TranslationLabels } from '../../types/labels.type';
import type { EventHandler, NullOr } from '../../global/utils.type';
import { Digest } from '../../global/index';
import { Deprecated } from '../../decorators/deprecated.decorator';
import { UIField } from '../field/ui-field';
import type { CustomFormComponent } from '../../types/custom-form-component';
import styles from './ui-searchinput.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISearchInput
 * @element ui-searchinput
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-searchinput</code> element.
 * Input field used for search purposes.
 * @fires event:clear
 * @property {boolean} [cleanable] {@attr cleanable} If true, field value can
 * be cleaned with button.
 * @property {boolean} [searchable] {@attr searchable} If true, a search
 * icon will be added.
 * @property {string} [controlId] {@attr control-id} Control (input element) id. Deprecated! Prefer setting id on the native input. If no id is set, control-id is used, then a generated id. Priority: native input id > control-id > uid.
 * @property {string} uuid Unique id of the control.
 * @property {HTMLInputElement} control Shortcut to control.
 * @property {HTMLButtonElement} clearButton Shortcut to clear button if present.
 * @property {HTMLDivElement} searchIconWrapper Shortcut to search icon.
 * @slot
 * @example
 * <ui-searchinput cleanable>
 *   <input name="search" type="search" id="test-render-id">
 * </ui-searchinput>
 */
@Component({
    tag: 'ui-searchinput',
    styles,
})
export class UISearchInput extends UIElement implements CustomFormComponent {
    @Deprecated('searchable is always true, kept for backward compatibility')
    @Attribute(Boolean)
    searchable = true;
    @Attribute(Boolean) cleanable!: boolean;
    @Attribute(String) controlId!: string;

    @Child('input') control!: HTMLInputElement;
    @Deprecated('use searchIconWrapper instead')
    @Child('.ui-searchinput__search-icon-wrapper')
    label!: HTMLDivElement;
    @Child('.ui-searchinput__search-icon-wrapper')
    searchIconWrapper!: HTMLDivElement;
    @Deprecated('use clearButton instead')
    @Child('.ui-searchinput__clear')
    clearBtn!: UIButton;
    @Child('.ui-searchinput__clear')
    clearButton!: UIButton;

    private inputFocusHandler!: EventHandler;
    private inputBlurHandler!: EventHandler;
    private inputUpdateHandler!: EventHandler;
    private clearHandler!: EventHandler;

    private _id?: string;
    private _hasUserSetNativeId = false;

    /**
     * Stable generated id when neither native id nor control-id is set.
     * @private
     */
    private get uid(): string {
        return (this._id ??= Digest.randomId());
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-searchinput', {
            clear: 'Clear input',
            search: 'Search',
        });
    }

    /**
     * Public API backward compatibility despite weird naming.
     * Priority: native input id > control-id > uid.
     */
    get uuid(): string {
        return (
            this.control?.id ??
            this.getAttribute('control-id') ??
            this.controlId ??
            this.uid
        );
    }

    /**
     * Returns the control id for ui-field (label[for], aria-describedby).
     * Priority: native input id > control-id > uid. Always returns a string.
     * @returns {string}
     */
    getControlIdForField(): string {
        const controlIdAttr =
            this.getAttribute('control-id') ?? this.controlId ?? null;
        const input =
            this.control ||
            this.querySelector<HTMLInputElement>('input') ||
            null;
        if (input) {
            const inputId = input.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (input.id !== controlIdAttr) {
                    input.id = controlIdAttr;
                    input.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = input.id || this.uid;
            if (input.id !== effectiveId) {
                input.id = effectiveId;
                input.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || this.uid;
    }

    /**
     * Handles clean event.
     * @param {Event} [e]
     * @private
     */
    handleClear(e?: Event) {
        const clearEvent = this.dispatchCustomEvent('clear', {}, true);
        if (clearEvent.defaultPrevented) {
            return;
        }
        hide(this.clearButton);
        setNativeInputValue(this.control, '');
        dispatchNativeEvent(this.control, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);
        this.control.focus();
    }

    /**
     * Updates appearance regarding input value.
     */
    updateStatus(): void {
        if (this.cleanable) {
            this.control.value
                ? show(this.clearButton)
                : hide(this.clearButton);
        } else {
            hide(this.clearButton);
        }
    }

    /**
     * Handles update on input.
     * @param {Event} [event]
     * @private
     */
    handleInputUpdates(event?: Event): void {
        this.updateStatus();
    }

    /**
     * Handles focus on input.
     * @param {Event} [event]
     * @private
     */
    handleInputFocus(event?: Event): void {
        this.classList.add('-focus');
    }

    /**
     * Handles blur on input.
     * @param {Event} [event]
     * @private
     */
    handleInputBlur(event?: Event): void {
        this.classList.remove('-focus');
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (
            name === 'control-id' &&
            this.control &&
            !this._hasUserSetNativeId
        ) {
            if (newValue) {
                this.control.id = newValue;
                this.control.setAttribute('id', newValue);
            } else {
                this.control.removeAttribute('id');
            }
        }
    }

    /**
     * @private
     */
    private renderClearButton(): NullOr<UIButton> {
        return createElement<UIButton>({
            tagName: 'ui-button',
            attributes: {
                'layout': UIButton.Layout.Tertiary,
                'aria-label': UISearchInput.labels.clear,
                'glyph': 'redesign-circle-x-mark',
                'type': 'button',
            },
            classList: {
                'ui-searchinput__clear': true,
            },
        });
    }

    /**
     * @private
     */
    private renderSearchIcon(): NullOr<HTMLDivElement> {
        return createElement<HTMLDivElement>({
            tagName: 'div',
            classList: {
                'ui-searchinput__search-icon-wrapper': true,
            },
            children: [
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'redesign-magnifying-glass',
                    },
                },
            ],
        });
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: string): void {
        setNativeInputValue(this.control, newValue);
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.control.disabled = newState;
    }

    /**
     * Checks whether this component is inside a ui-field that has a visible label.
     * @private
     */
    private hasParentFieldLabel(): boolean {
        const parentField: NullOr<UIField> = this.closest('ui-field');
        return !!(
            parentField?.label || parentField?.getAttribute('label')?.trim()
        );
    }

    /**
     * ui-searchinput may reach for the ui-field when the label has not been defined yet.
     * @private
     */
    private updateAriaLabel(): void {
        if (!this.control) {
            return;
        }
        // We keep custom aria-label even if ui-field's label is set. This is likely unwanted consequence because aria-label will override the visible label, but not doing so might disrupt developer expectations.
        if (
            this.hasParentFieldLabel() &&
            this.control.getAttribute('aria-label') ===
                (UISearchInput.labels.search as string)
        ) {
            this.control.removeAttribute('aria-label');
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.control.addEventListener('focus', this.inputFocusHandler);
        this.control.addEventListener('blur', this.inputBlurHandler);
        if (this.cleanable) {
            this.control.addEventListener('input', this.inputUpdateHandler);
            this.clearButton.addEventListener('click', this.clearHandler);
        }
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.control.removeEventListener('focus', this.inputFocusHandler);
        this.control.removeEventListener('blur', this.inputBlurHandler);
        if (this.cleanable) {
            this.control.removeEventListener('input', this.inputUpdateHandler);
            this.clearButton.removeEventListener('click', this.clearHandler);
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const input = this.querySelector<HTMLInputElement>('input');
        if (!input) {
            return;
        }

        this._hasUserSetNativeId = !!input.getAttribute('id')?.trim();
        if (!this._hasUserSetNativeId) {
            const effectiveId =
                this.getAttribute('control-id') ?? this.controlId ?? this.uid;
            input.id = effectiveId;
            input.setAttribute('id', effectiveId);
        }

        const clearButton = this.renderClearButton();
        const searchIcon = this.renderSearchIcon();

        insertElements(this, [
            {
                tagName: 'div',
                classList: {
                    'ui-searchinput__actions': true,
                },
                children: [clearButton, searchIcon],
            },
        ]);
        this.updateStatus();
        if (input.type !== 'search') {
            input.setAttribute('role', 'searchbox');
        }

        if (!this.hasParentFieldLabel() && !input.hasAttribute('aria-label')) {
            input.setAttribute(
                'aria-label',
                UISearchInput.labels.search as string
            );
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.inputFocusHandler = this.handleInputFocus.bind(this);
        this.inputBlurHandler = this.handleInputBlur.bind(this);
        this.inputUpdateHandler = this.handleInputUpdates.bind(this);
        this.clearHandler = this.handleClear.bind(this);
        this.subscribeEvents();
        this.updateAriaLabel();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
        this.updateAriaLabel();
    }
}
